import { Injectable } from '@angular/core';

export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage',
  MEMORY = 'memory',
}

export interface StorageOptions {
  type?: StorageType;
  encrypt?: boolean;
  ttl?: number; // Time to live in milliseconds
  prefix?: string;
}

export interface StorageItem<T = any> {
  value: T;
  timestamp: number;
  ttl?: number;
  encrypted?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly _memoryStorage = new Map<string, StorageItem>();
  private readonly _defaultOptions: StorageOptions = {
    type: StorageType.LOCAL,
    encrypt: false,
    prefix: 'app_',
  };

  private readonly _encryptionKey = 'your-secret-key-here'; // In production, use environment variable

  /**
   * Set a value in storage
   * @param key - Storage key
   * @param value - Value to store
   * @param options - Storage options
   */
  set<T>(key: string, value: T, options: StorageOptions = {}): void {
    const opts = { ...this._defaultOptions, ...options };
    const prefixedKey = this._getPrefixedKey(key, opts.prefix);
    const storageItem: StorageItem<T> = {
      value,
      timestamp: Date.now(),
      ttl: opts.ttl,
      encrypted: opts.encrypt,
    };

    const dataToStore = opts.encrypt
      ? this._encrypt(JSON.stringify(storageItem))
      : JSON.stringify(storageItem);

    switch (opts.type) {
      case StorageType.LOCAL:
        localStorage.setItem(prefixedKey, dataToStore);
        break;
      case StorageType.SESSION:
        sessionStorage.setItem(prefixedKey, dataToStore);
        break;
      case StorageType.MEMORY:
        this._memoryStorage.set(prefixedKey, storageItem);
        break;
    }
  }

  /**
   * Get a value from storage
   * @param key - Storage key
   * @param options - Storage options
   * @returns The stored value or null if not found/expired
   */
  get<T>(key: string, options: StorageOptions = {}): T | null {
    const opts = { ...this._defaultOptions, ...options };
    const prefixedKey = this._getPrefixedKey(key, opts.prefix);

    let data: string | null = null;
    let storageItem: StorageItem<T> | null = null;

    switch (opts.type) {
      case StorageType.LOCAL:
        data = localStorage.getItem(prefixedKey);
        break;
      case StorageType.SESSION:
        data = sessionStorage.getItem(prefixedKey);
        break;
      case StorageType.MEMORY:
        storageItem = this._memoryStorage.get(prefixedKey) || null;
        break;
    }

    if (opts.type === StorageType.MEMORY) {
      if (!storageItem) return null;
    } else {
      if (!data) return null;

      try {
        const decryptedData = opts.encrypt ? this._decrypt(data) : data;
        storageItem = JSON.parse(decryptedData);
      } catch (error) {
        console.error('Error parsing storage data:', error);
        return null;
      }
    }

    // Check if item has expired
    if (storageItem && storageItem.ttl) {
      const now = Date.now();
      const expirationTime = storageItem.timestamp + storageItem.ttl;

      if (now > expirationTime) {
        this.remove(key, options);
        return null;
      }
    }

    return storageItem?.value || null;
  }

  /**
   * Remove a value from storage
   * @param key - Storage key
   * @param options - Storage options
   */
  remove(key: string, options: StorageOptions = {}): void {
    const opts = { ...this._defaultOptions, ...options };
    const prefixedKey = this._getPrefixedKey(key, opts.prefix);

    switch (opts.type) {
      case StorageType.LOCAL:
        localStorage.removeItem(prefixedKey);
        break;
      case StorageType.SESSION:
        sessionStorage.removeItem(prefixedKey);
        break;
      case StorageType.MEMORY:
        this._memoryStorage.delete(prefixedKey);
        break;
    }
  }

  /**
   * Check if a key exists in storage
   * @param key - Storage key
   * @param options - Storage options
   * @returns True if key exists and is not expired
   */
  has(key: string, options: StorageOptions = {}): boolean {
    return this.get(key, options) !== null;
  }

  /**
   * Clear all storage of specified type
   * @param options - Storage options
   */
  clear(options: StorageOptions = {}): void {
    const opts = { ...this._defaultOptions, ...options };

    switch (opts.type) {
      case StorageType.LOCAL:
        this._clearByPrefix(opts.prefix, localStorage);
        break;
      case StorageType.SESSION:
        this._clearByPrefix(opts.prefix, sessionStorage);
        break;
      case StorageType.MEMORY:
        this._memoryStorage.clear();
        break;
    }
  }

  /**
   * Get all keys from storage
   * @param options - Storage options
   * @returns Array of keys
   */
  keys(options: StorageOptions = {}): string[] {
    const opts = { ...this._defaultOptions, ...options };
    const prefix = opts.prefix || '';

    switch (opts.type) {
      case StorageType.LOCAL:
        return this._getKeysByPrefix(prefix, localStorage);
      case StorageType.SESSION:
        return this._getKeysByPrefix(prefix, sessionStorage);
      case StorageType.MEMORY:
        return Array.from(this._memoryStorage.keys())
          .filter(key => key.startsWith(prefix))
          .map(key => key.replace(prefix, ''));
      default:
        return [];
    }
  }

  /**
   * Get storage size (number of items)
   * @param options - Storage options
   * @returns Number of items in storage
   */
  size(options: StorageOptions = {}): number {
    return this.keys(options).length;
  }

  /**
   * Clean expired items from storage
   * @param options - Storage options
   */
  cleanExpired(options: StorageOptions = {}): void {
    const keys = this.keys(options);

    keys.forEach(key => {
      this.get(key, options);
      // If get returns null, the item was expired and already removed
    });
  }

  // Private helper methods

  private _getPrefixedKey(key: string, prefix?: string): string {
    return prefix ? `${prefix}${key}` : key;
  }

  private _clearByPrefix(prefix = '', storage: Storage): void {
    const keys = this._getKeysByPrefix(prefix, storage);
    keys.forEach(key => storage.removeItem(key));
  }

  private _getKeysByPrefix(prefix: string, storage: Storage): string[] {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) {
        keys.push(key.replace(prefix, ''));
      }
    }
    return keys;
  }

  private _encrypt(text: string): string {
    // Simple XOR encryption - in production, use a proper encryption library
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^
          this._encryptionKey.charCodeAt(i % this._encryptionKey.length)
      );
    }
    return btoa(result); // Base64 encode
  }

  private _decrypt(encryptedText: string): string {
    try {
      const decoded = atob(encryptedText); // Base64 decode
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(
          decoded.charCodeAt(i) ^
            this._encryptionKey.charCodeAt(i % this._encryptionKey.length)
        );
      }
      return result;
    } catch (error) {
      console.error('Error decrypting data:', error);
      return encryptedText; // Return original if decryption fails
    }
  }
}
